import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtDuration, formatApiError, fyBounds } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ChevronDown, ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";

// ===== ADDED: TIMEZONE FIX UTILITIES =====
// Safely converts a JS Date object to "YYYY-MM-DD" in the device's local timezone
const toLocalDateString = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Converts a UTC ISO timestamp to local date string (YYYY-MM-DD)
const getLocalDateFromTimestamp = (isoString) => {
  if (!isoString) return null;
  const d = new Date(isoString);
  return toLocalDateString(d);
};

// Converts a UTC date string "YYYY-MM-DD" to the local device date string "YYYY-MM-DD"
const utcDateToLocal = (utcDateStr) => {
  if (!utcDateStr) return null;
  // We append T12:00:00Z (noon UTC) to prevent DST boundary issues from flipping the day
  const d = new Date(`${utcDateStr}T12:00:00Z`);
  return toLocalDateString(d);
};

// ==========================================


// Lime palette for charts (per user)
const LIME = "#84CC16";
const LIME_SHADES = ["#365314", "#4D7C0F", "#65A30D", "#84CC16", "#A3E635", "#BEF264", "#D9F99D", "#ECFCCB"];
// Finds the correct rate from the array based on the task date
const getRateForDate = (ratesArray, taskDate) => {
    if (!ratesArray || ratesArray.length === 0) return 0;
    
    // Extract just "YYYY-MM-DD" (in case taskDate has time attached like "2024-05-15T14:00:00")
    const dateStr = taskDate.substring(0, 10);

    // Find the FY where the task date falls
    for (const r of ratesArray) {
        if (r.start_date <= dateStr && dateStr <= r.end_date) {
            return r.rate || 0;
        }
    }
    
    // Fallback: If the date is outside all ranges, use the most recently added rate
    return ratesArray[ratesArray.length - 1]?.rate || 0;
};
const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This week" },
  { key: "last_week", label: "Last week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "this_year", label: "This year (FY)" },
  { key: "last_year", label: "Last year (FY)" },
  { key: "custom", label: "Custom" },
];

function presetToRange(key) {
  const today = new Date();
  const t = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const iso = toLocalDateString;
  if (key === "today") return { start: iso(t(today)), end: iso(t(today)) };
  if (key === "yesterday") {
    const y = new Date(today); y.setDate(y.getDate() - 1);
    return { start: iso(t(y)), end: iso(t(y)) };
  }
  if (key === "this_week" || key === "last_week") {
    const day = today.getDay();
    const monOffset = (day + 6) % 7;
    const ws = new Date(today); ws.setDate(today.getDate() - monOffset);
    if (key === "last_week") ws.setDate(ws.getDate() - 7);
    const we = new Date(ws); we.setDate(ws.getDate() + 6);
    return { start: iso(t(ws)), end: iso(t(we)) };
  }
  if (key === "this_month") {
    const s = new Date(today.getFullYear(), today.getMonth(), 1);
    const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { start: iso(s), end: iso(e) };
  }
  if (key === "last_month") {
    const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const e = new Date(today.getFullYear(), today.getMonth(), 0);
    return { start: iso(s), end: iso(e) };
  }
  if (key === "this_year") {
    const fy = fyBounds(today);
    return { start: fy.start, end: fy.end };
  }
  if (key === "last_year") {
    const prev = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    const fy = fyBounds(prev);
    return { start: fy.start, end: fy.end };
  }
  return null;
}

function shiftRange(range, dir) {
  const s = new Date(range.start + "T00:00:00");
  const e = new Date(range.end + "T00:00:00");
  const days = Math.round((e - s) / 86400000) + 1;
  const ns = new Date(s); ns.setDate(s.getDate() + dir * days);
  const ne = new Date(e); ne.setDate(e.getDate() + dir * days);
  return { start: toLocalDateString(ns), end: toLocalDateString(ne) }; // FIXED
}

export default function InsightsDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [preset, setPreset] = useState("today");
  const [range, setRange] = useState(() => presetToRange("today"));
  const [billability, setBillability] = useState("all"); // all | billable | nonbillable
  const [scope, setScope] = useState(isAdmin ? "team" : "me"); // me | team
  const [topN, setTopN] = useState(10);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);
  const presetRef = useRef(null);
  useEffect(() => {
    if (!presetOpen) return;
    const handler = (e) => { if (presetRef.current && !presetRef.current.contains(e.target)) setPresetOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [presetOpen]);

  useEffect(() => {
    Promise.all([api.get("/users"), api.get("/projects")])
      .then(([u, p]) => { setUsers(u.data); setProjects(p.data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const projectMap = useMemo(() => {
    const m = {};
    for (const p of projects) m[p.id] = p;
    return m;
  }, [projects]);

   const run = async () => {
    setLoading(true);
    try {
      // FIX: Send the exact date range directly, just like the working ReportsPage.
      // The backend correctly handles the DB query based on these exact dates.
      const filters = { start_date: range.start, end_date: range.end }; 
      
      // scope=me: backend already restricts members; for admin choosing "me", pass user_ids=[self]
      if (isAdmin && scope === "me") filters.user_ids = [user.id];
      
      const r = await api.post("/reports/run", filters);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, [range.start, range.end, scope]);

  const choosePreset = (k) => {
    setPreset(k);
    if (k !== "custom") setRange(presetToRange(k));
    setPresetOpen(false);
  };

  // Build derived data with billability filter applied
  const userRatesMap = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u.billable_rates || [];
    return m;
  }, [users]);

  const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

                const filtered = useMemo(() => {
    if (!data) return null;

    //const entries = data.entries || [];
    // Skip orphaned entries where project was deleted
    const entries = (data.entries || []).filter(e => e.project_name && e.project_name.trim() !== "");

    // 1. Filter entries by billability
    const filteredEntries = entries.filter((e) => {
      const proj = projectMap[e.project_id];
      const isBillable = !!proj?.billable;
      if (billability === "billable" && !isBillable) return false;
      if (billability === "nonbillable" && isBillable) return false;
      return true;
    });

    // 2. Calculate totals, billable seconds, and exact amounts
    let total = 0;
    let billableSecs = 0;
    let totalAmount = 0;
    const rawAmountPerProject = {};

    for (const e of filteredEntries) {
      total += e.duration_seconds;
      
      const proj = projectMap[e.project_id];
      const isBillable = !!proj?.billable;
      
      if (isBillable) {
        billableSecs += e.duration_seconds;
        const ratesArray = userRatesMap[e.user_id];
        const correctRate = getRateForDate(ratesArray, getLocalDateFromTimestamp(e.start_time));
        const amt = (e.duration_seconds / 3600) * correctRate;
        
        totalAmount += amt;
        rawAmountPerProject[e.project_id] = (rawAmountPerProject[e.project_id] || 0) + amt;
      }
    }

    // 3. Group by project with exact amounts
    const byProjectMap = {};
    for (const e of filteredEntries) {
      const pk = e.project_id || "none";
      if (!byProjectMap[pk]) {
        byProjectMap[pk] = {
          project_id: e.project_id,
          project_name: e.project_name,
          duration_seconds: 0,
          amount: 0
        };
      }
      byProjectMap[pk].duration_seconds += e.duration_seconds;
      byProjectMap[pk].amount = rawAmountPerProject[pk] || 0;
    }
    const by_project = Object.values(byProjectMap).sort((a, b) => b.duration_seconds - a.duration_seconds);

    // 4. Group by activity
    const byActivity = {};
    for (const e of filteredEntries) {
      const ak = `${e.description}__${e.project_id}`;
      byActivity[ak] = byActivity[ak] || { description: e.description, project_name: e.project_name, duration_seconds: 0 };
      byActivity[ak].duration_seconds += e.duration_seconds;
    }
    const activities = Object.values(byActivity).sort((a, b) => b.duration_seconds - a.duration_seconds);

    // 5. Daily breakdown
    const byDay = {};
    for (const e of filteredEntries) {
      const localDate = getLocalDateFromTimestamp(e.start_time);
      byDay[localDate] = (byDay[localDate] || 0) + e.duration_seconds;
    }
    
    const days = [];
    const start = new Date(range.start + "T00:00:00");
    const end = new Date(range.end + "T00:00:00");
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const localIso = toLocalDateString(d);
      days.push({ date: localIso, duration_seconds: byDay[localIso] || 0 });
    }

    return {
      total,
      total_amount: totalAmount,
      billable_pct: total > 0 ? Math.round((billableSecs / total) * 1000) / 10 : 0,
      days,
      by_project,
      activities,
    };
  }, [data, billability, projectMap, userRatesMap, range.start, range.end]);
  const dailyChart = (filtered?.days || []).map((d) => ({
    label: new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }), // FIXED: Ensure local parsing
    hours: +(d.duration_seconds / 3600).toFixed(2),
  }));

  const projectPie = (filtered?.by_project || []).slice(0, 8).map((p) => ({
    name: p.project_name, value: p.duration_seconds,
  }));

  const projectMaxSec = filtered?.by_project[0]?.duration_seconds || 1;

  return (
    <div data-testid="insights-page">
      <div className="min-h-20 border-b border-gray-200 bg-white sticky top-0 z-20 flex items-center pl-16 lg:pl-8 pr-4 sm:pr-8 py-3 justify-between gap-4 flex-wrap">
        <div>
          <div className="ess-section-label">Analyze</div>
          <div className="text-xl font-semibold tracking-tight text-[var(--ess-ink)]">Dashboard</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Billability */}
          <select value={billability} onChange={(e) => setBillability(e.target.value)} className="border border-gray-200 px-3 py-2 text-sm" data-testid="filter-billability">
            <option value="all">Billability — All</option>
            <option value="billable">Billable only</option>
            <option value="nonbillable">Non-billable only</option>
          </select>

          {/* Scope */}
          {isAdmin ? (
            <select value={scope} onChange={(e) => setScope(e.target.value)} className="border border-gray-200 px-3 py-2 text-sm" data-testid="filter-scope">
              <option value="team">All team</option>
              <option value="me">Only me</option>
            </select>
          ) : (
            <span className="border border-gray-200 px-3 py-2 text-sm bg-gray-50 text-gray-500" data-testid="filter-scope-locked">Only me</span>
          )}

          {/* Preset / Date range */}
          <div className="relative" ref={presetRef}>
            <button onClick={() => setPresetOpen((o) => !o)} className="border border-gray-200 px-3 py-2 text-sm flex items-center gap-2" data-testid="preset-trigger">
              <CalIcon size={14} />
              {PRESETS.find((p) => p.key === preset)?.label || "Custom"}
              <ChevronDown size={12} />
            </button>
            {presetOpen && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 z-30 shadow-md w-44">
                {PRESETS.map((p) => (
                  <button key={p.key} onClick={() => choosePreset(p.key)} className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 ${preset === p.key ? "bg-gray-50 font-semibold" : ""}`} data-testid={`preset-${p.key}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {preset === "custom" && (
            <>
              <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="border border-gray-200 px-2 py-2 text-sm" />
              <span className="text-xs text-gray-400">to</span>
              <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="border border-gray-200 px-2 py-2 text-sm" />
            </>
          )}
          <button onClick={() => setRange((r) => shiftRange(r, -1))} className="border border-gray-200 p-2 hover:bg-gray-50" data-testid="range-prev"><ChevronLeft size={14} /></button>
          <button onClick={() => setRange((r) => shiftRange(r, 1))} className="border border-gray-200 p-2 hover:bg-gray-50" data-testid="range-next"><ChevronRight size={14} /></button>
        </div>
      </div>

      <div className="px-4 sm:px-8 py-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 space-y-6">
          {/* KPIs */}
          <div className={`grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 ${isAdmin ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
            <div className="border border-gray-200 p-6">
              <div className="ess-section-label mb-2">Total time</div>
              <div className="text-4xl font-semibold tabular-nums text-[var(--ess-ink)]" data-testid="kpi-total">
                {filtered ? fmtDuration(filtered.total) : "—"}
              </div>
              <div className="text-xs text-gray-500 mt-2">{range.start} — {range.end}</div>
            </div>
            {isAdmin && (
              <div className="border border-gray-200 p-6">
                <div className="ess-section-label mb-2">Amount</div>
                <div className="text-3xl font-semibold tabular-nums text-[var(--ess-ink)]" data-testid="kpi-amount">
                  {filtered ? fmtINR(filtered.total_amount) : "—"}
                </div>
                <div className="text-xs text-gray-500 mt-2">billable hours × member rate</div>
              </div>
            )}
            <div className="border border-gray-200 p-6">
              <div className="ess-section-label mb-2">Billable</div>
              <div className="text-4xl font-semibold tabular-nums text-[var(--ess-ink)]" data-testid="kpi-billable">
                {filtered ? `${filtered.billable_pct}%` : "—"}
              </div>
              <div className="text-xs text-gray-500 mt-2">of total tracked time</div>
            </div>
          </div>

          {/* Daily bar chart */}
          <div className="border border-gray-200 p-6">
            <div className="ess-section-label mb-4">Daily breakdown</div>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={dailyChart} margin={{ top: 10, right: 10, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" stroke="#6B7280" interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} stroke="#6B7280" />
                  <Tooltip
                    cursor={{ fill: "#ECFCCB" }}
                    contentStyle={{ borderRadius: 0, border: "1px solid #1A1A1A", fontSize: 12 }}
                    formatter={(v) => [`${v}h`, "Hours"]}
                  />
                  <Bar dataKey="hours" fill={LIME} radius={[1, 1, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Project breakdown: donut + horizontal bars */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="border border-gray-200 p-6 flex items-center justify-center">
              <div className="relative" style={{ width: 240, height: 240 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={projectPie} dataKey="value" nameKey="name" innerRadius={70} outerRadius={110} paddingAngle={1} stroke="#fff">
                      {projectPie.map((_, i) => <Cell key={i} fill={LIME_SHADES[i % LIME_SHADES.length]} />)}
                    </Pie>
                    <Tooltip
                      contentStyle={{ borderRadius: 0, border: "1px solid #1A1A1A", fontSize: 12 }}
                      formatter={(v) => fmtDuration(v)}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400">Total</div>
                    <div className="text-lg font-semibold tabular-nums text-[var(--ess-ink)]">
                      {filtered ? fmtDuration(filtered.total) : "—"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 border border-gray-200 p-6">
              <div className="ess-section-label mb-4">By project</div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {(filtered?.by_project || []).slice(0, 20).map((p) => (
                  <div key={p.project_id || p.project_name} className={`grid ${isAdmin ? "grid-cols-12" : "grid-cols-12"} gap-2 items-center`} data-testid={`bp-${p.project_id}`}>
                    <div className="col-span-4 text-xs truncate text-[var(--ess-ink)] font-medium">{p.project_name}</div>
                    <div className={`${isAdmin ? "col-span-4" : "col-span-6"} bg-gray-100 h-3 relative overflow-hidden`}>
                      <div
                        className="absolute inset-y-0 left-0"
                        style={{ width: `${(p.duration_seconds / projectMaxSec) * 100}%`, background: LIME }}
                      />
                    </div>
                    <div className="col-span-2 text-xs tabular-nums text-right text-gray-600">{fmtDuration(p.duration_seconds)}</div>
                    {isAdmin && (
                      <div className="col-span-2 text-xs tabular-nums text-right text-[var(--ess-ink)] font-medium">{fmtINR(p.amount)}</div>
                    )}
                  </div>
                ))}
                {!filtered?.by_project?.length && (
                  <div className="text-sm text-gray-400 py-6 text-center">No data for this range.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar: Most tracked activities */}
        <aside className="border border-gray-200 p-5 h-fit lg:sticky lg:top-28">
          <div className="flex items-center justify-between mb-3">
            <div className="ess-section-label">Most tracked activities</div>
            <select value={topN} onChange={(e) => setTopN(+e.target.value)} className="text-xs border border-gray-200 px-2 py-1" data-testid="topn-select">
              {[5, 10, 20].map((n) => <option key={n} value={n}>Top {n}</option>)}
            </select>
          </div>
          {(filtered?.activities || []).slice(0, topN).map((a, i) => (
            <div key={i} className="py-3 border-t border-gray-100 first:border-t-0" data-testid={`activity-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-medium text-[var(--ess-ink)] line-clamp-2">{a.description}</div>
                <div className="text-xs tabular-nums text-gray-600 whitespace-nowrap">{fmtDuration(a.duration_seconds)}</div>
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: LIME }} />
                {a.project_name}
              </div>
            </div>
          ))}
          {(!filtered?.activities || filtered.activities.length === 0) && (
            <div className="text-sm text-gray-400 py-6 text-center">No activities yet.</div>
          )}
        </aside>
      </div>

      {loading && <div className="fixed bottom-4 right-4 text-xs uppercase tracking-wider bg-[var(--ess-ink)] text-white px-3 py-1.5">Loading…</div>}
    </div>
  );
}
